<template>
	<section
	@click="passDynamicLinksToVueRouter"
	class="editor custom-scrollbar"
	:class="{
		landing: landing,
		'editor-editing': isAdmin,
		'smooth-scroll': !editable && smooth,
	}"
	ref="editorContainer"
	>
	<div class="heading" ref="heading">
		<h1 class="title" v-if="!editable">{{ title }}</h1>
		<input
		class="title"
		v-if="editable"
		type="text"
		v-model="title"
		/>
		<button
		:class="{ 'sticky-btn': saveBtnSticky }"
		v-if="isAdmin && !editable"
		@click="editArticle"
		>
		<i class="fa-solid fa-pencil"></i>
	</button>
	<button
	:class="{ 'sticky-btn': saveBtnSticky }"
	v-if="isAdmin && editable"
	@click="saveArticle"
	>
	<i class="fa-solid fa-eye"></i>
</button>
</div>

<div class="aliases">
	<p class="bits" v-if="!editable">
		{{ aliases }}
	</p>
	<input
	v-if="editable"
	v-model="aliases"
	placeholder="Aliases"
	type="text"
	name="aliases"
	id="aliases"
	/>
	<input
	v-if="editable"
	v-model="code"
	type="text"
	name="code"
	id="code"
	placeholder="Permalink code"
	/>
</div>

<EditorMenu v-if="editable" :position="menuPosition" :editor="editor" />

<editor-content
class="editor__content"
:class="{ editing: editable }"
:editor="editor"
ref="editorContent"
/>

<ImageViewer ref="imgViewer" />
</section>
</template>

<script>
import ImageViewer from "./ImageViewer";
import { Editor, EditorContent } from "tiptap";
import {
	Blockquote,
	CodeBlock,
	HardBreak,
	HorizontalRule,
	OrderedList,
	BulletList,
	ListItem,
	TodoItem,
	TodoList,
	Bold,
	Code,
	Italic,
	Link,
	Strike,
	Underline,
	History,
	Table,
	TableHeader,
	TableCell,
	TableRow,
} from "tiptap-extensions";
import EditorMenu from "./EditorMenu";
import CustomHeading from "../../../util/tiptap-extensions/CustomHeading";
import GridCards from "../../../util/tiptap-extensions/GridCards";
import GridCard from "../../../util/tiptap-extensions/GridCard";
import ImagePaste from "../../../util/tiptap-extensions/ImagePaste";
import CardHeader from "../../../util/tiptap-extensions/CardHeader";
import CardContent from "../../../util/tiptap-extensions/CardContent";
import Iframe from "../../../util/tiptap-extensions/Iframe";

export default {
	name: "Editor",
	props: ["isAdmin", "article", "landing", "editable"],
	components: {
		EditorContent,
		EditorMenu,
		ImageViewer,
	},
	data() {
		return {
			editor: new Editor({
				editable: false,
				extensions: [
				new Blockquote(),
				new BulletList(),
				new CodeBlock(),
				new HardBreak(),
				new CustomHeading({ levels: [2, 3, 4, 5] }),
				new HorizontalRule(),
				new ListItem(),
				new OrderedList(),
				new TodoItem(),
				new TodoList(),
				new Link({ openOnClick: false }),
				new Bold(),
				new Code(),
				new Italic(),
				new Strike(),
				new Underline(),
				new History(),
				new ImagePaste({ clickHandler: this.onImageClick }),
				new GridCard(),
				new GridCards(),
				new CardHeader(),
				new CardContent(),
				new Iframe(),
				new Table({
					resizable: true,
				}),
				new TableHeader(),
				new TableCell(),
				new TableRow(),
				],
				content: "",
				onUpdate: ({ getJSON, getHTML }) => {
					this.json = getJSON();
					this.html = getHTML();
					this.debounceIncrement += 1;
					if (this.debounceIncrement % 50 == 0) {
						this.autoSaveArticle();
					}
				},
				onPaste: () => {
					this.autoSaveArticle();
				},
			}),
			title: "",
			interval: null,
			menuPosition: "normal",
			elementIntoViewId: "",
			debounceIncrement: 0,
			aliases: "",
			code: "",
			saveBtnSticky: false,
			smooth: true,
		};
	},
	mounted() {
		this.listenForScrolling();
		this.listenForHeaderPosition();
		this.checkEditable();
	},
	methods: {
		checkEditable: function () {
			if (this.editable) {
				this.startAutoSave();
			}
			this.editor.setOptions({
				editable: this.editable,
			});
		},
		saveArticle: function () {
			let newArticle = {
				...this.article,
				html: this.getHTML(),
				json: this.getJSON(),
				title: this.title,
				aliases: this.aliases ? this.aliases.split(",") : [],
				code: this.code,
			};
			newArticle.aliases = newArticle.aliases.filter((a) => a.length > 0);
			this.$emit("requestsave", newArticle);
		},
		editArticle: function () {
			this.$router.push({
				path: "/article-edit",
				query: {
					id: this.article.id,
					pos: this.$refs.editorContainer.scrollTop,
				},
			});
		},
		autoSaveArticle: function () {
			let newArticle = {
				...this.article,
				html: this.getHTML(),
				json: this.getJSON(),
				title: this.title,
				aliases: this.aliases ? this.aliases.split(",") : [],
				code: this.code,
			};
			newArticle.aliases = newArticle.aliases.filter((a) => a.length > 0);
			this.$emit("reqautosave", newArticle);
		},
		getJSON: function () {
			return this.editor.getJSON();
		},
		getHTML: function () {
			return this.editor.getHTML();
		},
		listenForScrolling: function () {
			this.$refs.editorContainer.addEventListener(
			"scroll",
			this.setMenuPosition
			);
		},
		listenForHeaderPosition: function () {
			this.$refs.editorContainer.addEventListener(
			"scroll",
			this.checkHeaderScroll
			);
		},
		setMenuPosition: function () {
			let menuOffsetTop = this.$refs.heading.offsetTop + 60;
			let scrollPosition = this.$refs.editorContainer.scrollTop;
			
			if (scrollPosition > menuOffsetTop + 35) {
				this.menuPosition = "sticky";
			} else {
				this.menuPosition = "normal";
			}
			
			this.saveBtnSticky = scrollPosition + 58 > menuOffsetTop;
		},
		checkHeaderScroll: function () {
			let editorContent = this.$refs.editorContent.$el;
			let editorContainer = this.$refs.editorContainer;
			
			//Dont emit anything if there is scroll can't reach first heading
			if (
			editorContent.offsetHeight <
			editorContainer.offsetHeight - 100
			) {
				this.$emit("itemscrolledinview", "");
				return;
			}
			
			let scrollPosition = editorContainer.scrollTop;
			let headerNodes = editorContainer.querySelectorAll(
			"h2[id], h3[id]"
			);
			let headerIds = [];
			let spacesFromTop = [];
			
			headerNodes.forEach((n) => {
				headerIds.push(n.id);
				spacesFromTop.push(n.offsetTop);
			});
			
			let firstFound = spacesFromTop.find((s) => s > scrollPosition - 80);
			let id = headerIds[spacesFromTop.indexOf(firstFound) - 1];
			
			if (id && this.elementIntoViewId != id) {
				this.elementIntoViewId = id;
				this.$emit("itemscrolledinview", id);
			}
		},
		startAutoSave: function () {
			this.interval = setInterval(() => {
				this.autoSaveArticle();
			}, 10000);
		},
		stopAutoSave: function () {
			clearInterval(this.interval);
		},
		passDynamicLinksToVueRouter: function (event) {
			// ensure we use the link, in case the click has been received by a subelement
			let { target } = event;
			while (target && target.tagName !== "A") target = target.parentNode;
			// handle only links that occur inside the component and do not reference external resources
			if (
			target &&
			target.matches(".editor a:not([href*='://'])") &&
			target.href
			) {
				// some sanity checks taken from vue-router:
				// https://github.com/vuejs/vue-router/blob/dev/src/components/link.js#L106
				const {
					altKey,
					ctrlKey,
					metaKey,
					shiftKey,
					button,
					defaultPrevented,
				} = event;
				if (this.editable) return;
				// don't handle with control keys
				if (metaKey || altKey || ctrlKey || shiftKey) return;
				// don't handle when preventDefault called
				if (defaultPrevented) return;
				// don't handle right clicks
				if (button !== undefined && button !== 0) return;
				// don't handle if `target="_blank"`
				if (target && target.getAttribute) {
					const linkTarget = target.getAttribute("target");
					if (/\b_blank\b/i.test(linkTarget)) return;
				}
				// don't handle same page links/anchors
				const url = new URL(target.href);
				//add the query to the path
				const to = url.pathname + url.search;
				//get the current router state
				let currentRoute = this.$route.fullPath;
				
				if (window.location.pathname !== to && event.preventDefault) {
					event.preventDefault();
					if (currentRoute != to) {
						this.$router.push(to);
					}
				}
			} else if (target && target.href) {
				event.preventDefault();
				window.open(new URL(target.href), "_newtab");
			}
		},
		resetScroll: function () {
			this.smooth = false;
			let pos = this.$route.query.pos || 0;
			setTimeout(() => {
				this.$refs.editorContainer.scrollTop = pos;
				this.smooth = true;
			}, 0);
		},
		setAliases: function () {
			if (this.article.aliases) {
				this.aliases = this.article.aliases.join(",");
			} else {
				this.aliases = "";
			}
		},
		setCode: function () {
			if (this.article.code) {
				this.code = this.article.code;
			} else {
				this.code = "";
			}
		},
		onImageClick: function (event) {
			let { target } = event;
			if (target && target.tagName === "IMG" && target.src) {
				this.$refs.imgViewer.show(target.src);
			}
		},
	},
	watch: {
		article: function () {
			this.editor.setContent(this.article.html);
			this.title = this.article.title;
			this.setAliases();
			this.setCode();
			this.resetScroll();
		},
	},
	beforeDestroy() {
		this.editor.destroy();
		this.stopAutoSave();
		this.$refs.editorContainer.removeEventListener(
		"scroll",
		this.setMenuPosition
		);
	},
};
</script>

<style>

.editor {
	padding: 2rem;
	overflow: auto;
	position: relative;
	scrollbar-color: #999 #F8F8F8;
	
	&.smooth-scroll {
		scroll-behavior: smooth;
	}
	
	img {
		cursor: pointer;
	}

	.heading, .aliases {	
		display: flex;
		gap: 1.5em;
		padding-bottom: 1.5em;

		input {
			flex-grow: 1;
			border: 2px solid #ccc;
			border-radius: 0.3rem;
			padding: 1rem 1.5rem;
			outline: none;

			&:focus {
				border-color: var(--clr-accent-primary);
			}

		}
	}

	.heading {

		button {
			margin-left: auto;
			background-color: transparent;
			border: none;
			cursor: pointer;
		}

		input {
			font-size: 2em;
		}
	}

	.aliases {
	}
	
}

.editor__button {
	background: transparent;
	border: none;
	opacity: 0.4;
	transition: opacity 0.3s ease-in;
}

.editor__button:hover {
	opacity: 1;
}

.editor__button:focus {
	outline: none;
}

.editor__content.editing {
	border: 1px dashed var(--clr-accent-secondary);
	border-radius: 0.2em;
	padding: 1em 1.2em;
}

.editor__content pre {
	padding: .7rem 1rem;
	border-radius: .2em;
	background: #EEE;
	color: #111;
	overflow-x: auto;
	border: 1px solid #ccc;
}

.ml-5px {
	margin-left: 5px;
}

.landing .ProseMirror {
	min-height: unset;
}

.landing .editor {
	height: auto;
}

.sticky-btn {
	position: fixed;
	display: flex;
	top: 4rem;
	right: 1rem;
	z-index: 51;
	align-items: center;
	justify-content: center;
	border: 1px solid var(--clr-accent-secondary);
	border-radius: 0.2em;
	padding: 0.5rem 1rem;
	background-color: #fff;
	font-size: 1.7em;
}

.tableWrapper {
	pointer-events: none;
}

.editing .tableWrapper {
	pointer-events: all;
}

</style>